import React from "react";
import { Link } from "react-router-dom";
import { Telescope, FlaskConical, TrendingUp } from "lucide-react";
import { Layout } from "../components/tag/Layout";
import { HeroBg, EyebrowPill, SectionPill, CheckRow } from "../components/tag/Primitives";
import { LogEntryCard } from "../components/tag/LogEntryCard";
import { api } from "../lib/api";

const counters = [
  { n: "6", l: "Ecosystems Built" },
  { n: "6", l: "Niches Tested" },
  { n: "847", l: "Articles Published" },
  { n: "23", l: "Tools Built" },
];

const labs = [
  {
    sector: "Healthcare",
    name: "MetabolicLabOS",
    accent: "#1a6b3a",
    nameColor: "#3aab5a",
    status: { dot: "#3aab5a", text: "● LIVE" },
    items: [
      "300+ SEO articles across 10 content hubs",
      "AI food and symptom logging (Kai)",
      "Stripe subscription billing",
    ],
    learning: "Health content compounds fastest with pharmacological specificity.",
  },
  {
    sector: "Education",
    name: "BeaconsIEP",
    accent: "#1a3a8b",
    nameColor: "#5a8aeb",
    status: { dot: "#5a8aeb", text: "● LIVE" },
    items: [
      "AI companion Grace trained on IEP advocacy",
      "22 digital guides (12 paid, 10 free)",
      "Bilingual English/Spanish",
    ],
    learning: "Underserved niches convert lead magnets at 3× average.",
  },
  {
    sector: "Finance",
    name: "Build Ready",
    accent: "#7a3a00",
    nameColor: "#ba7a30",
    status: { dot: "#ba7a30", text: "◐ IN DEV" },
    items: [
      "AI coach Atlas across 7 construction phases",
      "Loan eligibility and budget tools",
      "Digital product suite",
    ],
    learning: "Hypothesis: trust-heavy niches need tools before content.",
  },
  {
    sector: "Compliance",
    name: "AASC",
    accent: "#4a1a7a",
    nameColor: "#9a6adb",
    status: { dot: "#9a6adb", text: "● LIVE" },
    items: [
      "AI governance certification framework",
      "Compliance content library",
      "Enterprise positioning",
    ],
    learning: "Positioning before market maturity is the entire advantage.",
  },
  {
    sector: "Sports",
    name: "ChalkOS",
    accent: "#8b1a1a",
    nameColor: "#eb5a5a",
    status: { dot: "#eb5a5a", text: "● LIVE" },
    items: [
      "Full SaaS platform",
      "3 subscription tiers",
      "League and multi-team management",
    ],
    learning: "AuthorityOS can produce software, not just content.",
  },
  {
    sector: "Enterprise",
    name: "Kaiya Solutions",
    accent: "#0d4a6b",
    nameColor: "#3a9acb",
    status: { dot: "#3a9acb", text: "● LIVE" },
    items: [
      "Enterprise platform — 11 modules",
      "300,000+ lines of code",
      "Governance and risk infrastructure",
    ],
    learning: "Complexity is its own form of credibility signal.",
  },
];

const logPreviewFallback = [];

const beliefs = [
  { num: "I.", text: "We believe expertise should not lose to ", bold: "algorithms." },
  { num: "II.", text: "We believe the best people in an industry should not be the ", bold: "hardest to find." },
  { num: "III.", text: "We believe authority is not a personality trait. It is a ", bold: "system." },
  { num: "IV.", text: "We believe that system can be ", bold: "built, measured, and replicated." },
  { num: "V.", text: "We believe expertise should ", bold: "win again." },
];

const Section = ({ children, style, ...rest }) => (
  <section
    style={{
      background: "#090909",
      borderTop: "1px solid #1a1a1a",
      padding: "44px 32px",
      ...style,
    }}
    {...rest}
  >
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>{children}</div>
  </section>
);

const FloatingDashboard = () => (
  <div
    data-testid="hero-dashboard-card"
    style={{
      background: "#0f0f0f",
      border: "1px solid #1a1a1a",
      borderRadius: "12px 12px 0 0",
      overflow: "hidden",
      margin: "0 12px",
      maxWidth: 1000,
      marginLeft: "auto",
      marginRight: "auto",
    }}
  >
    {/* Window bar */}
    <div style={{ background: "#0b0b0b", borderBottom: "1px solid #161616", padding: "10px 16px", display: "flex", gap: 7 }}>
      <span style={{ width: 9, height: 9, background: "#222", borderRadius: "50%" }} />
      <span style={{ width: 9, height: 9, background: "#222", borderRadius: "50%" }} />
      <span style={{ width: 9, height: 9, background: "#222", borderRadius: "50%" }} />
    </div>
    <div
      className="tag-dashboard-body"
      style={{ display: "grid", gridTemplateColumns: "148px 1fr", minHeight: 180 }}
    >
      {/* Sidebar */}
      <div style={{ background: "#0b0b0b", borderRight: "1px solid #161616", padding: 16 }}>
        <div style={{ display: "flex", gap: 7, alignItems: "center", marginBottom: 18 }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#FF6575" }} />
          <span style={{ color: "#ccc", fontWeight: 600, fontSize: 12 }}>TAG Labs</span>
        </div>
        {["Dashboard", "Portfolio", "Builder's Log", "Assessment", "AuthorityOS"].map((label, i) => (
          <div
            key={label}
            style={{
              display: "flex",
              gap: 8,
              padding: "7px 9px",
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 500,
              background: i === 0 ? "#161616" : "transparent",
              color: i === 0 ? "#bbb" : "#777",
              marginBottom: 3,
            }}
          >
            <span style={{ width: 13, height: 13, background: "#1e1e1e", borderRadius: 3, flexShrink: 0 }} />
            <span>{label}</span>
          </div>
        ))}
      </div>
      {/* Main */}
      <div style={{ padding: 16 }}>
        <div style={{ fontSize: 9, color: "#666", textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600, marginBottom: 10, display: "flex", gap: 6 }}>
          <span>LABS</span><span>/</span><span>HEALTHCARE</span><span>/</span><span style={{ color: "#FF6575" }}>METABOLICLABOS</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14, alignItems: "center" }}>
          <div style={{ display: "flex", gap: 7, alignItems: "center" }}>
            <span style={{ width: 11, height: 11, background: "#FF6575", borderRadius: 3 }} />
            <span style={{ color: "#ccc", fontSize: 13, fontWeight: 600 }}>Authority Ecosystem</span>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button style={{ background: "#FF6575", color: "#fff", fontSize: 9, fontWeight: 700, padding: "5px 11px", borderRadius: 5, border: "none" }}>Edit</button>
            <button style={{ background: "#151515", border: "1px solid #222", color: "#666", fontSize: 9, padding: "5px 11px", borderRadius: 5 }}>View</button>
          </div>
        </div>
        <div style={{ display: "flex", gap: 14, borderBottom: "1px solid #161616", paddingBottom: 9, marginBottom: 14 }}>
          <span style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: "#444" }}>Overview</span>
          <span style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: "#aaa" }}>Authority Score</span>
          <span style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: "#444" }}>Assets</span>
        </div>
        <div style={{ background: "#0b0b0b", border: "1px solid #161616", borderRadius: 7, padding: 12 }}>
          <div style={{ display: "flex", gap: 7, alignItems: "center", marginBottom: 10 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#FF6575" }} />
            <span style={{ color: "#666", fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>CONTENT OUTPUT — 2024</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 3, marginBottom: 5 }}>
            {["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"].map((m) => (
              <span key={m} style={{ fontSize: 7, color: "#444", textAlign: "center", fontWeight: 600, letterSpacing: "0.04em" }}>{m}</span>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 3, alignItems: "end" }}>
            {[
              { id: "jan", h: 12, c: "#1a1a1a" },
              { id: "feb", h: 16, c: "#1a1a1a" },
              { id: "mar", h: 20, c: "#1a1a1a" },
              { id: "apr", h: 26, c: "#1a1a1a" },
              { id: "may", h: 30, c: "#FF6575", o: 0.5 },
              { id: "jun", h: 36, c: "#FF6575", o: 0.7 },
              { id: "jul", h: 18, c: "#1a1a1a" },
              { id: "aug", h: 18, c: "#1a1a1a" },
              { id: "sep", h: 18, c: "#1a1a1a" },
              { id: "oct", h: 18, c: "#1a1a1a" },
              { id: "nov", h: 18, c: "#1a1a1a" },
              { id: "dec", h: 18, c: "#1a1a1a" },
            ].map((b) => (
              <span key={b.id} style={{ height: b.h, background: b.c, borderRadius: 3, opacity: b.o ?? 1 }} />
            ))}
          </div>
        </div>
      </div>
    </div>
    <style>{`
      @media (max-width: 720px) {
        .tag-dashboard-body { grid-template-columns: 1fr !important; }
      }
    `}</style>
  </div>
);

export default function Home() {
  const [logEntries, setLogEntries] = React.useState(logPreviewFallback);
  const [logLoading, setLogLoading] = React.useState(true);

  React.useEffect(() => {
    api
      .get("/log")
      .then((res) => setLogEntries((res.data || []).slice(0, 5)))
      .catch(() => setLogEntries([]))
      .finally(() => setLogLoading(false));
  }, []);

  return (
    <Layout title="The Authority Gap | Help expertise win again" path="/">
      {/* HERO */}
      <section
        data-testid="home-hero"
        style={{
          background: "#090909",
          minHeight: 520,
          textAlign: "center",
          padding: "70px 32px 0",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <HeroBg />
        <div style={{ position: "relative", zIndex: 1, maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ marginBottom: 24 }}>
            <EyebrowPill>Est. 2024 — An ongoing experiment in expert authority</EyebrowPill>
          </div>
          <h1 className="tag-h1" data-testid="home-hero-h1" style={{ marginBottom: 18 }}>
            <span>You already know something worth</span>
            <br />
            <span style={{ color: "#999" }}>building a business around.</span>
          </h1>
          <p style={{ color: "#aaa", fontSize: 15, marginBottom: 6 }}>
            Most people never package what they know. We help you turn it into something people can find, learn from, and pay for.
          </p>
          <p style={{ color: "#FF6575", fontSize: 15, fontWeight: 500, marginBottom: 32 }}>
            The framework is AuthorityOS. We build it with you, or we build it for you.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <Link to="/authoriteos" data-testid="hero-cta-framework" className="btn-primary">
              See The Framework
            </Link>
            <Link to="/score" data-testid="hero-cta-score" className="btn-ghost">
              Take the Assessment →
            </Link>
          </div>
          <div style={{ color: "#777", fontSize: 11, marginBottom: 40, marginTop: 16 }}>
            Free assessment ✤ No credit card required
          </div>
        </div>
        <FloatingDashboard />
      </section>

      {/* COUNTERS */}
      <section style={{ background: "#090909", borderTop: "1px solid #1a1a1a", padding: "24px 32px" }}>
        <div
          className="tag-counters-grid"
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            textAlign: "center",
          }}
        >
          {counters.map((c, i) => (
            <div
              key={c.l}
              data-testid={`counter-${i}`}
              style={{
                borderRight: i < counters.length - 1 ? "1px solid #1a1a1a" : "none",
                padding: "0 16px",
              }}
            >
              <div style={{ fontSize: 26, fontWeight: 700, color: "#fff", letterSpacing: "-0.02em", marginBottom: 4 }}>{c.n}</div>
              <div style={{ fontSize: 10, fontWeight: 600, color: "#999", textTransform: "uppercase", letterSpacing: "0.08em" }}>{c.l}</div>
            </div>
          ))}
        </div>
        <style>{`
          @media (max-width: 700px) {
            .tag-counters-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 24px; }
            .tag-counters-grid > div { border-right: none !important; }
          }
        `}</style>
      </section>

      {/* PILLARS */}
      <Section>
        <div className="tag-pillars" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 32 }}>
          {[
            { id: "telescope", Icon: Telescope, text: "We study why experts lose online and why less qualified voices win." },
            { id: "flask", Icon: FlaskConical, text: "We run experiments across industries and document what works." },
            { id: "trending", Icon: TrendingUp, text: "We build systems that make expertise visible and compounding." },
          ].map((p, i) => (
            <div key={p.id} data-testid={`pillar-${i}`} style={{ textAlign: "center" }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  background: "#111",
                  border: "1px solid #222",
                  borderRadius: "50%",
                  margin: "0 auto 16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <p.Icon size={18} color="#FF6575" />
              </div>
              <p style={{ fontSize: 13, color: "#bbb", lineHeight: 1.65, maxWidth: 200, margin: "0 auto" }}>{p.text}</p>
            </div>
          ))}
        </div>
        <style>{`
          @media (max-width: 760px) {
            .tag-pillars { grid-template-columns: 1fr !important; gap: 32px !important; }
          }
        `}</style>
      </Section>

      {/* SECTION 4 — VISIBILITY ECONOMY */}
      <Section>
        <div className="tag-two-col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 44, alignItems: "center" }}>
          <div>
            <div style={{ marginBottom: 16 }}><SectionPill>The Visibility Economy</SectionPill></div>
            <h2 className="tag-h2" style={{ marginBottom: 18 }}>
              <span>Who gets more attention?</span><br />
              <span style={{ color: "#777" }}>You already know the answer.</span>
            </h2>
            <p className="tag-body" style={{ marginBottom: 18 }}>
              A 20-year physician with no content strategy loses to a 6-month creator with a posting schedule. This isn&apos;t a talent problem. It&apos;s a systems problem.
            </p>
            <CheckRow>The gap exists in every credentialed field</CheckRow>
            <CheckRow>It widens every year as AI floods content</CheckRow>
            <CheckRow>It closes with the right system</CheckRow>
          </div>
          <div className="tag-card">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <span style={{ color: "#aaa", fontWeight: 600, fontSize: 13 }}>Authority Gap Analysis</span>
              <span style={{ color: "#444", fontSize: 18 }}>···</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderBottom: "1px solid #1e1e1e", marginBottom: 16, paddingBottom: 16 }}>
              <div>
                <div style={{ color: "#fff", fontSize: 24, fontWeight: 700 }}>12</div>
                <div style={{ color: "#888", fontSize: 11, fontWeight: 500 }}>Years experience</div>
              </div>
              <div>
                <div style={{ color: "#FF6575", fontSize: 24, fontWeight: 700 }}>23</div>
                <div style={{ color: "#888", fontSize: 11, fontWeight: 500 }}>Authority Score</div>
              </div>
            </div>
            {[
              "No SEO content — invisible to search",
              "No email list — no owned audience",
              "No lead magnet — no capture mechanism",
            ].map((t) => (
              <div key={t} className="tag-row-item" style={{ display: "flex", gap: 9, marginBottom: 7, alignItems: "center" }}>
                <span style={{ width: 16, height: 16, borderRadius: "50%", background: "#FF6575", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff" }} />
                </span>
                <span style={{ color: "#bbb", fontSize: 12, fontWeight: 500 }}>{t}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* SECTION 5 — AUTHORITIOS INTRO */}
      <Section>
        <div className="tag-two-col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 44, alignItems: "center" }}>
          <div className="tag-card">
            <div style={{ display: "flex", gap: 18, alignItems: "center", marginBottom: 16 }}>
              <div
                style={{
                  width: 68,
                  height: 68,
                  border: "7px solid #1e1e1e",
                  borderRadius: "50%",
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    inset: -7,
                    borderRadius: "50%",
                    border: "7px solid transparent",
                    borderTopColor: "#FF6575",
                    borderRightColor: "#FF6575",
                    transform: "rotate(-30deg)",
                  }}
                />
                <span style={{ color: "#FF6575", fontSize: 14, fontWeight: 700, position: "relative", zIndex: 1 }}>74%</span>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ color: "#888", fontSize: 11, fontWeight: 500, marginBottom: 4 }}>Authority Score</div>
                <div style={{ color: "#fff", fontSize: 20, fontWeight: 700, marginBottom: 3 }}>MetabolicLabOS</div>
                <div style={{ color: "#FF6575", fontSize: 11, fontWeight: 500 }}>↑ +12 since launch</div>
              </div>
            </div>
            <div className="tag-row-item" style={{ display: "flex", gap: 11, alignItems: "center" }}>
              <div style={{ width: 30, height: 30, background: "#1a1a1a", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ color: "#666", fontSize: 14 }}>♡</span>
              </div>
              <div>
                <div style={{ color: "#bbb", fontSize: 12, fontWeight: 600 }}>Healthcare niche</div>
                <div style={{ color: "#777", fontSize: 10 }}>AuthorityOS — Active build</div>
              </div>
              <div style={{ width: 36, height: 3, background: "#1e1e1e", borderRadius: 2, marginLeft: "auto" }} />
            </div>
          </div>
          <div>
            <div style={{ marginBottom: 16 }}><SectionPill>AuthorityOS</SectionPill></div>
            <h2 className="tag-h2" style={{ marginBottom: 18 }}>
              <span>A system that makes expertise</span><br />
              <span style={{ color: "#777" }}>impossible to ignore.</span>
            </h2>
            <p className="tag-body" style={{ marginBottom: 18 }}>
              AuthorityOS is the framework we built across six industries. It turns expertise into digital assets that compound — search traffic, email lists, tools, and products that work 24 hours a day.
            </p>
            <CheckRow>Proven across healthcare, education, finance</CheckRow>
            <CheckRow>Builds assets you own — not rented platforms</CheckRow>
            <CheckRow>Compounds over time without paid ads</CheckRow>
          </div>
        </div>
        <style>{`
          @media (max-width: 880px) {
            .tag-two-col { grid-template-columns: 1fr !important; gap: 28px !important; }
          }
        `}</style>
      </Section>

      {/* LABS GRID */}
      <Section>
        <div style={{ textAlign: "center", marginBottom: 30 }}>
          <div style={{ marginBottom: 14 }}><SectionPill>The Experiments</SectionPill></div>
          <h2 className="tag-h2" style={{ marginBottom: 10 }}>The Labs</h2>
          <p style={{ fontSize: 13, color: "#aaa", fontStyle: "italic" }}>Six industries. One framework. Running in public.</p>
        </div>
        <div className="tag-labs-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
          {labs.map((lab) => (
            <div key={lab.name} data-testid={`lab-card-${lab.name.toLowerCase().replace(/\s/g, "")}`} className="tag-lab-card">
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: lab.accent }} />
              <div style={{ position: "absolute", top: 14, right: 14, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: lab.status.dot }}>
                {lab.status.text}
              </div>
              <div style={{ fontSize: 9, color: "#888", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, marginBottom: 7 }}>{lab.sector}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: lab.nameColor, marginBottom: 12 }}>{lab.name}</div>
              <div style={{ height: 1, background: "#1a1a1a", marginBottom: 12 }} />
              {lab.items.map((it) => (
                <div key={it} style={{ display: "flex", gap: 7, alignItems: "baseline", marginBottom: 4 }}>
                  <span style={{ width: 3, height: 3, background: "#FF6575", borderRadius: "50%", flexShrink: 0, marginTop: 5 }} />
                  <span style={{ fontSize: 11, color: "#aaa", fontWeight: 500 }}>{it}</span>
                </div>
              ))}
              <div style={{ color: "#FF6575", fontStyle: "italic", fontSize: 11, lineHeight: 1.55, borderTop: "1px solid #1a1a1a", marginTop: 12, paddingTop: 10 }}>
                {lab.learning}
              </div>
            </div>
          ))}
        </div>
        <style>{`
          @media (max-width: 900px) { .tag-labs-grid { grid-template-columns: repeat(2, 1fr) !important; } }
          @media (max-width: 600px) { .tag-labs-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </Section>

      {/* FRAMEWORK */}
      <section style={{ background: "#090909", borderTop: "1px solid #1a1a1a", padding: "44px 32px", position: "relative", overflow: "hidden" }}>
        <div className="tag-dot-pattern" />
        <div style={{ position: "relative", zIndex: 1, maxWidth: 400, margin: "0 auto", textAlign: "center" }}>
          <div style={{ marginBottom: 14 }}><SectionPill>The Framework</SectionPill></div>
          <h2 className="tag-h2" style={{ marginBottom: 26 }}>AuthorityOS</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 26, textAlign: "left" }}>
            {[
              { bg: "#121212", border: "#222", color: "#FF6575", text: "The Authority Gap — TAG" },
              { bg: "#0f0f0f", border: "#1a1a1a", color: "#aaa", text: "AuthorityOS — The Framework" },
              { bg: "#0c0c0c", border: "#161616", color: "#888", text: "Authority Assets — What gets built" },
              { bg: "#FF6575", border: "transparent", color: "#fff", text: "Authority — The outcome" },
            ].map((t, i, arr) => (
              <React.Fragment key={t.text}>
                <div style={{ background: t.bg, border: `1px solid ${t.border}`, color: t.color, padding: "12px 18px", borderRadius: 7, fontSize: 12, fontWeight: 600 }}>{t.text}</div>
                {i < arr.length - 1 && (
                  <div style={{ color: "#333", paddingLeft: 18, fontSize: 11, lineHeight: 1 }}>↓</div>
                )}
              </React.Fragment>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6, textAlign: "left" }}>
            {[
              { n: "01", t: "Content" },
              { n: "02", t: "Tools" },
              { n: "03", t: "Email" },
              { n: "04", t: "Products" },
              { n: "05", t: "AI" },
              { n: "06", t: "Distribution" },
              { n: "07", t: "Search" },
              { n: "+", t: "More", dashed: true },
            ].map((c) => (
              <div
                key={c.n + c.t}
                style={{
                  background: "#0f0f0f",
                  border: `1px ${c.dashed ? "dashed" : "solid"} ${c.dashed ? "#444" : "#1a1a1a"}`,
                  borderRadius: 6,
                  padding: "8px 9px",
                  fontSize: 11,
                  color: c.dashed ? "#444" : "#aaa",
                  fontWeight: 600,
                  display: "flex",
                  gap: 5,
                }}
              >
                <span style={{ fontSize: 9, color: "#FF6575", fontWeight: 700 }}>{c.n}</span>
                <span>{c.t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LOG PREVIEW */}
      <Section>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 22 }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, letterSpacing: "-0.02em", color: "#fff", margin: 0 }}>Builder&apos;s Log</h2>
          <Link to="/log" data-testid="log-preview-view-all" style={{ fontSize: 12, color: "#777", fontWeight: 500 }}>View all entries →</Link>
        </div>
        <div data-testid="home-log-preview">
          {logLoading && (
            <div className="tag-card" style={{ textAlign: "center", color: "#777" }}>Loading entries…</div>
          )}
          {!logLoading && logEntries.length === 0 && (
            <div className="tag-card" style={{ textAlign: "center", color: "#bbb" }}>
              The first entry drops this week. Visit the <Link to="/log" style={{ color: "#FF6575" }}>full log</Link> to subscribe.
            </div>
          )}
          {!logLoading && logEntries.map((e) => <LogEntryCard key={e.id || e.entry_number} entry={e} />)}
        </div>
      </Section>

      {/* MANIFESTO */}
      <section style={{ background: "#090909", borderTop: "1px solid #1a1a1a", padding: "56px 32px", position: "relative", overflow: "hidden" }}>
        <div className="tag-manifesto-glow" />
        <div style={{ position: "relative", zIndex: 1, maxWidth: 900, margin: "0 auto" }}>
          <div style={{ color: "#FF6575", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: 32 }}>
            THE MANIFESTO
          </div>
          <div>
            {beliefs.map((b, i) => (
              <div
                key={b.num}
                style={{
                  display: "flex",
                  gap: 18,
                  padding: "18px 0",
                  borderBottom: "1px solid #141414",
                  borderTop: i === 0 ? "1px solid #141414" : "none",
                  alignItems: "baseline",
                }}
              >
                <span style={{ fontSize: 11, color: "#555", fontWeight: 700, letterSpacing: "0.04em", minWidth: 24, flexShrink: 0 }}>{b.num}</span>
                <span style={{ fontSize: 18, color: "#aaa", lineHeight: 1.4, fontWeight: 400 }}>
                  {b.text}
                  <span style={{ color: "#fff", fontWeight: 500 }}>{b.bold}</span>
                </span>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 14, marginTop: 40, alignItems: "center" }}>
            <span style={{ color: "#777", fontSize: 12, fontStyle: "italic" }}>Join the movement. Follow the experiments. Build with us.</span>
            <Link to="/score" data-testid="manifesto-cta" className="btn-outline-accent">JOIN THE AUTHORITY GAP</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
